/** Points for picking the lie correctly. */
export const SCORE_CORRECT_GUESS = 10;
/** Points per guesser who picked a truth (fooled by the bluffer). */
export const SCORE_PER_PLAYER_FOOLED = 5;
/** Extra points when every eligible guesser was fooled (none picked the lie). */
export const SCORE_BONUS_ALL_PLAYERS_FOOLED = 10;

const SLOT_KEYS = ["truth1", "truth2", "lie"];

/**
 * @param {object} params
 * @param {string[]} params.eligibleGuessers
 * @param {Map<string, object>} params.userMap
 * @param {string} params.subjectUserId
 * @param {number} params.guessEpoch
 * @param {boolean} [params.bonusOnly] When true, only the "all fooled" subject bonus is returned (per-guess points are applied at pick time).
 * @returns {{ deltas: Map<string, number>, fooledCount: number, submittedCount: number, allFooledBonus: boolean }}
 */
export function computeTruthRoundScores({
  eligibleGuessers,
  userMap,
  subjectUserId,
  guessEpoch,
  bonusOnly = false,
}) {
  const deltas = new Map();
  let fooledCount = 0;
  const guesses = [];

  for (const id of eligibleGuessers) {
    const d = userMap.get(id);
    if (!d) continue;
    const ge = Number(d.guessEpoch ?? 0) || 0;
    if (ge !== guessEpoch) continue;
    const pick = String(d.guessPick ?? "").trim();
    if (!SLOT_KEYS.includes(pick)) continue;
    guesses.push({ id, pick });
  }

  if (bonusOnly) {
    const allEligibleSubmitted =
      eligibleGuessers.length > 0 && guesses.length === eligibleGuessers.length;
    const allFooled =
      allEligibleSubmitted &&
      guesses.length > 0 &&
      guesses.every((g) => g.pick !== "lie");
    if (allFooled && subjectUserId) {
      deltas.set(subjectUserId, SCORE_BONUS_ALL_PLAYERS_FOOLED);
    }
    fooledCount = guesses.filter((g) => g.pick !== "lie").length;
    return {
      deltas,
      fooledCount,
      submittedCount: guesses.length,
      allFooledBonus: Boolean(allFooled),
    };
  }

  for (const { id, pick } of guesses) {
    if (pick === "lie") {
      deltas.set(id, (deltas.get(id) || 0) + SCORE_CORRECT_GUESS);
    } else {
      fooledCount += 1;
    }
  }

  let subjectDelta = fooledCount * SCORE_PER_PLAYER_FOOLED;
  const allEligibleSubmitted =
    eligibleGuessers.length > 0 && guesses.length === eligibleGuessers.length;
  const allFooled =
    allEligibleSubmitted && guesses.length > 0 && guesses.every((g) => g.pick !== "lie");
  if (allFooled) {
    subjectDelta += SCORE_BONUS_ALL_PLAYERS_FOOLED;
  }

  if (subjectUserId && subjectDelta > 0) {
    deltas.set(subjectUserId, (deltas.get(subjectUserId) || 0) + subjectDelta);
  }

  return {
    deltas,
    fooledCount,
    submittedCount: guesses.length,
    allFooledBonus: Boolean(allFooled),
  };
}
